/**
 * Product Update Pulse Report Templates
 * Based on best practices from Notion, Linear, Figma, Slack
 */

import { PulseReportTemplate } from "./types";

export const FEATURE_RELEASE: PulseReportTemplate = {
  id: "feature-release",
  name: "New Feature Announcement",
  reportType: "product_update",
  description: "Announce new chatbot features and capabilities",
  purpose:
    "Excite users about new features and drive immediate adoption with clear value propositions.",
  icon: "RocketOutlined",
  color: "#8b5cf6",
  suggestedHtmlTemplateId: "feature-release",
  suggestedSubject: "New: {{feature_name}} is here!",
  defaultSchedule: {
    type: "on_event",
    eventType: "feature_release",
  },
  defaultDataSections: {
    billing: false,
    usage: false,
    performance: false,
    analytics: false,
    team: false,
  },
  targetAudience: "All users who would benefit from the feature",
  expectedOutcomes: [
    "Rapid feature adoption",
    "Increased platform usage",
    "User excitement and engagement",
    "Reduced feature discovery time",
  ],
  bestPractices: [
    "Lead with the user benefit, not the feature",
    "Include a clear CTA to try it now",
    "Show a quick preview or GIF",
    "Explain how to get started in 3 steps",
  ],
  keyMetrics: [
    "Feature adoption rate",
    "Time to first use",
    "Click-through rate",
    "User feedback",
  ],
  tags: ["product", "feature", "release", "announcement", "new"],
  bestFor: ["Feature launches", "Product announcements", "Adoption campaigns"],
  difficulty: "beginner",
  setupTime: "10 minutes",
  previewDescription: "Exciting new feature announcement",
};

export const CHATBOT_IMPROVEMENTS: PulseReportTemplate = {
  id: "chatbot-improvements",
  name: "Chatbot AI Improvements",
  reportType: "product_update",
  description: "Announce AI and chatbot intelligence upgrades",
  purpose:
    "Communicate improvements to chatbot AI, response quality, and conversation handling.",
  icon: "RobotOutlined",
  color: "#10b981",
  suggestedHtmlTemplateId: "feature-release",
  suggestedSubject: "Your chatbots just got smarter",
  defaultSchedule: {
    type: "on_event",
    eventType: "ai_update",
  },
  defaultDataSections: {
    billing: false,
    usage: false,
    performance: true,
    analytics: false,
    team: false,
  },
  targetAudience: "All chatbot owners",
  expectedOutcomes: [
    "Trust in AI capabilities",
    "Increased chatbot deployment",
    "Better conversation outcomes",
    "Reduced manual intervention",
  ],
  bestPractices: [
    "Show before/after examples",
    "Quantify the improvement",
    "Highlight specific use cases",
    "Encourage testing with real conversations",
  ],
  keyMetrics: [
    "Resolution rate improvement",
    "Response accuracy",
    "User satisfaction delta",
    "Conversation completion rate",
  ],
  tags: ["product", "ai", "chatbot", "improvement", "intelligence"],
  bestFor: ["AI updates", "Quality improvements", "Trust building"],
  difficulty: "intermediate",
  setupTime: "10 minutes",
  previewDescription: "AI and chatbot improvement announcement",
};

export const INTEGRATION_LAUNCH: PulseReportTemplate = {
  id: "integration-launch",
  name: "New Integration Available",
  reportType: "product_update",
  description: "Announce new integrations and connection options",
  purpose:
    "Promote new integrations that extend chatbot capabilities and workflow automation.",
  icon: "ApiOutlined",
  color: "#0ea5e9",
  suggestedHtmlTemplateId: "feature-release",
  suggestedSubject: "New Integration: Connect your chatbot to {{integration_name}}",
  defaultSchedule: {
    type: "on_event",
    eventType: "integration_launch",
  },
  defaultDataSections: {
    billing: false,
    usage: false,
    performance: false,
    analytics: false,
    team: false,
  },
  targetAudience: "Users of the integrated platform",
  expectedOutcomes: [
    "Integration adoption",
    "Expanded use cases",
    "Increased platform stickiness",
    "Workflow automation",
  ],
  bestPractices: [
    "Segment by users of target platform",
    "Show specific workflow examples",
    "Include setup documentation link",
    "Offer setup assistance",
  ],
  keyMetrics: [
    "Integration connections",
    "Activation rate",
    "Data sync volume",
    "User satisfaction",
  ],
  tags: ["product", "integration", "api", "connection", "automation"],
  bestFor: ["Integration launches", "Partner announcements", "Ecosystem growth"],
  difficulty: "intermediate",
  setupTime: "10 minutes",
  previewDescription: "New integration announcement",
};

export const PRODUCT_CHANGELOG: PulseReportTemplate = {
  id: "product-changelog",
  name: "Monthly Changelog",
  reportType: "product_update",
  description: "Monthly roundup of all product updates and fixes",
  purpose:
    "Keep users informed about all improvements, fixes, and changes made to the platform.",
  icon: "FileTextOutlined",
  color: "#6366f1",
  suggestedHtmlTemplateId: "changelog",
  suggestedSubject: "What's New in {{current_month}} - Changelog",
  defaultSchedule: {
    type: "monthly",
    dayOfMonth: 1,
    time: "10:00",
  },
  defaultDataSections: {
    billing: false,
    usage: false,
    performance: false,
    analytics: false,
    team: false,
  },
  targetAudience: "All users",
  expectedOutcomes: [
    "Transparency and trust",
    "Feature awareness",
    "Reduced surprise from changes",
    "Developer community engagement",
  ],
  bestPractices: [
    "Categorize by type (features, fixes, improvements)",
    "Link to detailed documentation",
    "Highlight user-requested features",
    "Keep it scannable",
  ],
  keyMetrics: [
    "Open rate",
    "Documentation clicks",
    "Feature adoption",
    "Support ticket trends",
  ],
  tags: ["product", "changelog", "updates", "releases", "transparency"],
  bestFor: ["Product transparency", "Developer relations", "Change communication"],
  difficulty: "beginner",
  setupTime: "10 minutes",
  previewDescription: "Monthly product changelog digest",
};

export const PRODUCT_UPDATE_TEMPLATES = [
  FEATURE_RELEASE,
  CHATBOT_IMPROVEMENTS,
  INTEGRATION_LAUNCH,
  PRODUCT_CHANGELOG,
];
